import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Mail, Phone, Lock, User, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const AuthModal = () => {
  const navigate = useNavigate();
  const { authModalOpen, closeAuthModal, authMode, switchAuthMode, signup, login, verify } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verificationStep, setVerificationStep] = useState(false);
  const [verificationContact, setVerificationContact] = useState('');
  
  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  
  const [loginData, setLoginData] = useState({
    email_or_phone: '',
    password: ''
  });
  
  const [verificationData, setVerificationData] = useState({
    email_or_phone: '',
    verification_code: ''
  });

  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);

  const resetForms = () => {
    setSignupData({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
    setLoginData({ email_or_phone: '', password: '' });
    setVerificationData({ email_or_phone: '', verification_code: '' });
    setForgotPasswordEmail('');
    setForgotPasswordSent(false);
    setVerificationStep(false);
    setVerificationContact('');
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) return;
    
    // Validation
    if (!signupData.name || !signupData.email || !signupData.phone || !signupData.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Basic phone number validation
    if (signupData.phone && signupData.phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    if (signupData.password !== signupData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (signupData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      const result = await signup({
        name: signupData.name,
        email: signupData.email,
        phone: signupData.phone,
        password: signupData.password
      });
      
      if (result.success) {
        // Skip verification step - account is ready
        resetForms();
        switchAuthMode('login');
        toast.success('Account created! Please login now.');
      }
    } catch (error) {
      console.error('Signup error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) return;
    
    // Basic validation
    if (!loginData.email_or_phone || !loginData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      const result = await login(loginData);
      if (result.success) {
        resetForms();
        // AuthContext will handle redirect based on postLoginRedirect in sessionStorage
        // If no redirect is set, user stays on current page
      }
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await verify(verificationData);
    
    if (result.success) {
      setVerificationStep(false);
      resetForms();
      switchAuthMode('login');
      toast.success('Account verified! Please login now.');
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (loading) return;
    
    if (!forgotPasswordEmail || !forgotPasswordEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const API = `${BACKEND_URL}/api`;
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: forgotPasswordEmail
      });
      
      setForgotPasswordSent(true);
      toast.success('Password Reset Link sent to your email successfully');
    } catch (error) {
      console.error('Forgot password error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send reset link. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };


  const handleModalClose = () => {
    resetForms();
    closeAuthModal();
  };

  return (
    <Dialog open={authModalOpen} onOpenChange={handleModalClose}>
      <DialogContent className="w-[95vw] max-w-md mx-4 sm:mx-auto">
        {verificationStep ? (
          <div>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-green-600" />
                Verify Your Account
              </DialogTitle>
              <DialogDescription>
                We sent a 6-digit code to <strong>{verificationContact}</strong>
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleVerification} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="verification_code">Verification Code</Label>
                <Input
                  id="verification_code"
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={verificationData.verification_code}
                  onChange={(e) => setVerificationData({
                    ...verificationData,
                    verification_code: e.target.value
                  })}
                  maxLength={6}
                  className="text-center text-lg tracking-widest"
                  required
                  data-testid="verification-code-input"
                />
              </div>
              
              <Button 
                type="submit" 
                disabled={loading || verificationData.verification_code.length !== 6}
                className="w-full bg-gradient-to-r from-green-500 to-orange-500 hover:from-green-600 hover:to-orange-600"
                data-testid="verify-account-btn"
              >
                {loading ? 'Verifying...' : 'Verify Account'}
              </Button>
              
              <p className="text-sm text-center text-gray-600">
                Didn't receive the code?{' '}
                <button 
                  type="button" 
                  className="text-green-600 hover:text-green-700 font-medium"
                  onClick={() => toast.info('Resend functionality coming soon')}
                >
                  Resend
                </button>
              </p>
            </form>
          </div>
        ) : (
          <div>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                {authMode === 'forgot-password' ? 'Reset Your Password' : 'Welcome to Desi Pet Recipes'}
              </DialogTitle>
              <DialogDescription>
                {authMode === 'login' ? 'Sign in to your account' : authMode === 'forgot-password' ? 'Enter your email to receive a password reset link' : 'Create your account to get started'}
              </DialogDescription>
            </DialogHeader>
            
            {authMode === 'forgot-password' ? (
              <div className="mt-6">
                {forgotPasswordSent ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Mail className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Check Your Email</h3>
                    <p className="text-gray-600 mb-6">
                      We've sent a password reset link to <strong>{forgotPasswordEmail}</strong>
                    </p>
                    <Button
                      onClick={() => {
                        setForgotPasswordSent(false);
                        setForgotPasswordEmail('');
                        switchAuthMode('login');
                      }}
                      variant="outline"
                    >
                      Back to Login
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div>
                      <Label htmlFor="forgot-email" className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Email Address
                      </Label>
                      <Input
                        id="forgot-email"
                        type="email"
                        placeholder="Enter your email"
                        value={forgotPasswordEmail}
                        onChange={(e) => setForgotPasswordEmail(e.target.value)}
                        required
                      />
                    </div>
                    
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        onClick={() => switchAuthMode('login')}
                        variant="outline"
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-gradient-to-r from-green-500 to-orange-500 hover:from-green-600 hover:to-orange-600"
                      >
                        {loading ? 'Sending...' : 'Send Reset Link'}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            ) : (
            <Tabs value={authMode} onValueChange={switchAuthMode} className="mt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="signup" data-testid="signup-tab">Sign Up</TabsTrigger>
              </TabsList>
              
              <TabsContent value="login">
                <Card className="border-0 shadow-none">
                  <CardContent className="p-0 pt-4">
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                        <Label htmlFor="login-email" className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Email
                        </Label>
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="Enter your email"
                          value={loginData.email_or_phone}
                          onChange={(e) => setLoginData({
                            ...loginData,
                            email_or_phone: e.target.value
                          })}
                          required
                          data-testid="login-email-input"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="login-password" className="flex items-center gap-2">
                          <Lock className="w-4 h-4" />
                          Password
                        </Label>
                        <div className="relative">
                          <Input
                            id="login-password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter your password"
                            value={loginData.password}
                            onChange={(e) => setLoginData({
                              ...loginData,
                              password: e.target.value
                            })}
                            required
                            data-testid="login-password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <button
                          type="button"
                          onClick={() => switchAuthMode('forgot-password')}
                          className="text-sm text-green-600 hover:text-green-700 hover:underline"
                        >
                          Forgot Password?
                        </button>
                      </div>
                      
                      <Button 
                        type="submit" 
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-green-500 to-orange-500 hover:from-green-600 hover:to-orange-600"
                        data-testid="login-submit-btn"
                      >
                        {loading ? 'Signing In...' : 'Sign In'}
                      </Button>
                      
                      {/* Divider */}
                      <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-gray-300"></span>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-white text-gray-500">or continue with</span>
                        </div>
                      </div>
                      
                      {/* Google Sign In Button */}
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full flex items-center justify-center gap-2 border-gray-300 hover:bg-gray-50"
                        onClick={() => {
                          const redirectUrl = window.location.origin + '/pets';
                          window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                        }}
                        data-testid="google-signin-btn"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Sign in with Google
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="signup">
                <Card className="border-0 shadow-none">
                  <CardContent className="p-0 pt-4">
                    <form onSubmit={handleSignup} className="space-y-4">
                      <div>
                        <Label htmlFor="signup-name" className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          Full Name
                        </Label>
                        <Input
                          id="signup-name"
                          type="text"
                          placeholder="Enter your full name"
                          value={signupData.name}
                          onChange={(e) => setSignupData({
                            ...signupData,
                            name: e.target.value
                          })}
                          required
                          data-testid="signup-name-input"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="signup-email" className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Email Address
                        </Label>
                        <Input
                          id="signup-email"
                          type="email"
                          placeholder="Enter your email"
                          value={signupData.email}
                          onChange={(e) => setSignupData({
                            ...signupData,
                            email: e.target.value
                          })}
                          required
                          data-testid="signup-email-input"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="signup-phone" className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          Phone Number (International)
                        </Label>
                        <Input
                          id="signup-phone"
                          type="tel"
                          placeholder="+[country code][number] (e.g., +919876543210, +14155552671)"
                          value={signupData.phone}
                          onChange={(e) => setSignupData({
                            ...signupData,
                            phone: e.target.value
                          })}
                          required
                          data-testid="signup-phone-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Include country code with + (India: +91, USA: +1, UK: +44, etc.)</p>
                      </div>
                      
                      <div>
                        <Label htmlFor="signup-password" className="flex items-center gap-2">
                          <Lock className="w-4 h-4" />
                          Password
                        </Label>
                        <div className="relative">
                          <Input
                            id="signup-password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Create a password (min. 6 characters)"
                            value={signupData.password}
                            onChange={(e) => setSignupData({
                              ...signupData,
                              password: e.target.value
                            })}
                            required
                            data-testid="signup-password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="signup-confirm-password" className="flex items-center gap-2">
                          <Lock className="w-4 h-4" />
                          Confirm Password
                        </Label>
                        <div className="relative">
                          <Input
                            id="signup-confirm-password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Confirm your password"
                            value={signupData.confirmPassword}
                            onChange={(e) => setSignupData({
                              ...signupData,
                              confirmPassword: e.target.value
                            })}
                            required
                            data-testid="signup-confirm-password-input"
                            className={
                              signupData.confirmPassword && signupData.password
                                ? signupData.password === signupData.confirmPassword
                                  ? 'border-green-500 focus:ring-green-500'
                                  : 'border-red-500 focus:ring-red-500'
                                : ''
                            }
                          />
                          {signupData.confirmPassword && signupData.password && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                              {signupData.password === signupData.confirmPassword ? (
                                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )}
                            </div>
                          )}
                        </div>
                        {signupData.confirmPassword && signupData.password && signupData.password !== signupData.confirmPassword && (
                          <p className="text-sm text-red-500 mt-1">Passwords do not match</p>
                        )}
                        {signupData.confirmPassword && signupData.password && signupData.password === signupData.confirmPassword && (
                          <p className="text-sm text-green-500 mt-1">Passwords match ✓</p>
                        )}
                      </div>
                      
                      <Button 
                        type="submit" 
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-green-500 to-orange-500 hover:from-green-600 hover:to-orange-600"
                        data-testid="signup-submit-btn"
                      >
                        {loading ? 'Creating Account...' : 'Create Account'}
                      </Button>
                      
                      {/* Divider */}
                      <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-gray-300"></span>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-white text-gray-500">or sign up with</span>
                        </div>
                      </div>
                      
                      {/* Google Sign Up Button */}
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full flex items-center justify-center gap-2 border-gray-300 hover:bg-gray-50"
                        onClick={() => {
                          const redirectUrl = window.location.origin + '/pets';
                          window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                        }}
                        data-testid="google-signup-btn"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Sign up with Google
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;